export function useImageHelper() {
  /**
   * Re-encodes an image file for security sanitization and format standardization.
   * This process removes potentially malicious content, strips EXIF metadata, and ensures
   * the image is in a clean, safe format before uploading to the backend.
   *
   * @param file - The original image file to be sanitized
   * @returns A new sanitized File object with the same visual content but cleaned metadata
   */
  async function reEncodeImage(file: File): Promise<File> {
    // Load file as data URL
    const dataUrl = await fileToDataUrl(file);

    const preLoadedImage = await loadImage(dataUrl);

    const canvas = document.createElement('canvas');
    canvas.width = preLoadedImage.width;
    canvas.height = preLoadedImage.height;
    const canvasContext = canvas.getContext('2d');

    if (!canvasContext) {
      throw new Error('Failed to get canvas context');
    }

    canvasContext.drawImage(preLoadedImage, 0, 0);
    const blob = await canvasToBlob(canvas, file.type);

    return new File([blob], file.name, { type: file.type });
  }

  async function fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  async function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to load image'));
      image.src = src;
    });
  }

  async function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      }, type);
    });
  }

  return { reEncodeImage };
}
